import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ChevronLeft, RefreshCw, ShieldAlert, Search, Unlink } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  SimpleTable,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  type ColumnDef,
} from '@/components/ui'

interface CalendarUserSettings {
  outlookToTanssEnabled: boolean
  createFailures: Array<{
    m365EventId: string
    failCount: number
    blockedUntil: string | null
    isCurrentlyBlocked: boolean
    lastError: string | null
  }>
}

interface DiagnosticsUserSummary {
  id: string
  email: string
  name: string
  tanssId: string | null
  m365UserId: string | null
  hasM365Token: boolean
}

interface DiagnosticsCurrentUser extends DiagnosticsUserSummary {
  graphMe: {
    id: string
    mail: string | null
    userPrincipalName: string | null
    displayName: string | null
  } | null
  graphMeError: string | null
  resolvedTanssEmployeeId: number | null
  resolvedTanssSource: 'users.tanss_id' | 'session-fallback' | 'unresolved'
  tanssError: string | null
  mismatches: string[]
}

interface DiagnosticsConflicts {
  duplicateTanssIds: Array<{ tanssId: string; users: DiagnosticsUserSummary[] }>
  usersWithoutTanssIdButM365: DiagnosticsUserSummary[]
  duplicateM365UserIds: Array<{ m365UserId: string; users: DiagnosticsUserSummary[] }>
  tokenMailboxMismatches: Array<{
    user: DiagnosticsUserSummary
    graphMail: string | null
    graphUpn: string | null
    note: string
  }>
}

interface DiagnosticsCalendarLink {
  id: string
  entityType: string
  entityId: string
  origin: string
  m365EventId: string
  originalTitle: string | null
  lastSyncedAt: string | null
  createdAt: string | null
  tanssEmployeeId: number | null
  tanssEmployeeIdMatchesUser: boolean | null
  tanssLookupError: string | null
}

interface DiagnosticsResponse {
  generatedAt: string
  currentUser: DiagnosticsCurrentUser
  conflicts: DiagnosticsConflicts | null
  links: DiagnosticsCalendarLink[]
  isAdmin: boolean
  tanssCrossCheckPerformed: boolean
}

interface InternalUserRow {
  id: string
  email: string
  name: string
  isActive: boolean
  portalKind: string
}

interface MirrorTanssSupport {
  id: number
  employeeId: number
  planningType: string
  text: string | null
  title: string | null
  date: number
  duration: number | null
  ticketId: number | null
}

interface MirrorOutlookEvent {
  subject: string | null
  start: string | null
  end: string | null
  bodyPreview: string | null
  webLink: string | null
  isAllDay: boolean | null
}

interface MirrorEntry {
  linkId: string
  m365EventId: string
  tanssSupportId: string
  entityType: string
  origin: string
  originalTitle: string | null
  originalStart: string | null
  originalEnd: string | null
  lastSyncedAt: string | null
  createdAt: string | null
  tanssSupport: MirrorTanssSupport | null
  tanssLookupError: string | null
  tanssWebUrl: string | null
  outlookEvent: MirrorOutlookEvent | null
  outlookLookupError: string | null
}

interface UserMirrorsResponse {
  user: DiagnosticsUserSummary
  since: string
  days: number
  totalCount: number
  liveDetailsLoaded: boolean
  entries: MirrorEntry[]
}

function extractErrorMessage(err: unknown): string {
  const axErr = err as { response?: { data?: unknown }; message?: string }
  const data = axErr?.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
  }
  return axErr?.message ?? ''
}

function formatDateTime(value: string | null): string {
  if (!value) return '–'
  const d = new Date(value)
  if (isNaN(d.getTime())) return value
  return d.toLocaleString('de-DE')
}

function tanssSourceLabel(source: DiagnosticsCurrentUser['resolvedTanssSource']): string {
  switch (source) {
    case 'users.tanss_id':
      return 'users.tanss_id (sicher)'
    case 'session-fallback':
      return 'TANSS-Login-Fallback (UNSICHER)'
    case 'unresolved':
      return 'nicht aufloesbar'
  }
}

function KeyValueRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <SimpleTable.Tr>
      <SimpleTable.Th className="w-1/3 bg-gray-50">{label}</SimpleTable.Th>
      <SimpleTable.Td>{value}</SimpleTable.Td>
    </SimpleTable.Tr>
  )
}

export function CalendarDiagnosticsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<DiagnosticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [crossCheck, setCrossCheck] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)
  const [userSettings, setUserSettings] = useState<CalendarUserSettings | null>(null)
  const [settingsSaving, setSettingsSaving] = useState(false)

  const loadUserSettings = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get<CalendarUserSettings>('/calendar-sync/user-settings')
      setUserSettings(res)
    } catch {
      // non-critical — nicht blockieren
    }
  }, [])

  const toggleOutlookToTanss = useCallback(async (enabled: boolean) => {
    setSettingsSaving(true)
    try {
      await apiClient.patch('/calendar-sync/user-settings', { outlookToTanssEnabled: enabled })
      setUserSettings((prev) => prev ? { ...prev, outlookToTanssEnabled: enabled } : prev)
    } catch (err) {
      const msg = extractErrorMessage(err)
      alert(`Einstellung konnte nicht gespeichert werden: ${msg}`)
    } finally {
      setSettingsSaving(false)
    }
  }, [])

  const clearAllBlockedEvents = useCallback(async () => {
    try {
      await apiClient.delete('/calendar-sync/user-settings/create-failures?all=true')
      await loadUserSettings()
    } catch (err) {
      const msg = extractErrorMessage(err)
      alert(`Sperren konnten nicht aufgehoben werden: ${msg}`)
    }
  }, [loadUserSettings])

  const load = useCallback(async (withCrossCheck: boolean) => {
    setLoading(true)
    setError(null)
    setErrorDetails(null)
    try {
      const { data: res } = await apiClient.get<DiagnosticsResponse>(
        `/calendar-sync/diagnostics${withCrossCheck ? '?crossCheck=1' : ''}`,
      )
      setData(res)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Diagnose konnte nicht geladen werden.${msg ? ' ' + msg : ''}`)
      setErrorDetails(msg || null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(false)
    void loadUserSettings()
  }, [load, loadUserSettings])

  const hasOwnIssues = (data?.currentUser.mismatches.length ?? 0) > 0
  const hasGlobalIssues = useMemo(() => {
    if (!data?.conflicts) return false
    const c = data.conflicts
    return (
      c.duplicateTanssIds.length > 0 ||
      c.usersWithoutTanssIdButM365.length > 0 ||
      c.duplicateM365UserIds.length > 0 ||
      c.tokenMailboxMismatches.length > 0
    )
  }, [data])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kalender-Sync Diagnose"
        description="Pruefe die User-Trennung beim Outlook↔TANSS-Sync. Es wird verglichen, welches M365-Konto Dein gespeicherter Token tatsaechlich ansteuert (Graph /me), welche TANSS-Employee-ID der Sync fuer Dich verwendet und ob die gespiegelten TANSS-Supports wirklich auf Dich zeigen."
        breadcrumbs={[
          { label: 'Einstellungen', to: '/einstellungen' },
          { label: 'Kalender-Diagnose' },
        ]}
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<ChevronLeft className="w-4 h-4" />}
              onClick={() => navigate('/einstellungen')}
            >
              Zurueck
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<RefreshCw className="w-4 h-4" />}
              loading={loading}
              onClick={() => void load(crossCheck)}
            >
              Neu laden
            </Button>
          </>
        }
      />

      {/* Outlook→TANSS Einstellungen */}
      {userSettings && (
        <Card>
          <Card.Header>
            <h2 className="text-h4 text-gray-900">Sync-Einstellungen</h2>
          </Card.Header>
          <Card.Body className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-body font-medium">Outlook-Termine nach TANSS spiegeln</p>
                <p className="text-caption text-gray-500 mt-0.5">
                  Wenn aktiv, werden neue Outlook-Termine automatisch als TANSS-Büro-Einträge (APPOINTMENT_PRIVATE) angelegt.
                  Deaktivieren stoppt die Outlook→TANSS-Richtung sofort — bestehende Verknüpfungen bleiben erhalten,
                  es werden nur keine neuen TANSS-Einträge mehr angelegt.
                </p>
              </div>
              <Switch
                checked={userSettings.outlookToTanssEnabled}
                onChange={(e) => void toggleOutlookToTanss(e.target.checked)}
                disabled={settingsSaving}
              />
            </div>
            {userSettings.createFailures.length > 0 && (
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-caption font-medium text-gray-700">
                    Temporär gesperrte Events ({userSettings.createFailures.filter((f) => f.isCurrentlyBlocked).length} aktiv gesperrt,{' '}
                    {userSettings.createFailures.length} gesamt)
                  </p>
                  <Button variant="ghost" size="xs" onClick={() => void clearAllBlockedEvents()}>
                    Alle entsperren
                  </Button>
                </div>
                <p className="text-caption text-gray-500 mb-2">
                  Events die wiederholt nicht in TANSS angelegt werden konnten, werden für {24}h gesperrt um Loops zu verhindern.
                </p>
                <SimpleTable>
                  <SimpleTable.Body>
                    {userSettings.createFailures.map((f) => (
                      <SimpleTable.Tr key={f.m365EventId}>
                        <SimpleTable.Td>
                          <code className="text-xs break-all">{f.m365EventId.slice(0, 40)}…</code>
                        </SimpleTable.Td>
                        <SimpleTable.Td>
                          {f.isCurrentlyBlocked ? (
                            <span className="text-red-600 text-xs font-medium">
                              gesperrt bis {f.blockedUntil ? formatDateTime(f.blockedUntil) : '–'}
                            </span>
                          ) : (
                            <span className="text-orange-600 text-xs">{f.failCount}x fehlgeschlagen</span>
                          )}
                        </SimpleTable.Td>
                        <SimpleTable.Td className="text-xs text-gray-500 max-w-xs truncate">
                          {f.lastError?.slice(0, 100) ?? '–'}
                        </SimpleTable.Td>
                      </SimpleTable.Tr>
                    ))}
                  </SimpleTable.Body>
                </SimpleTable>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Tabs defaultValue="diagnose">
        <TabsList>
          <TabsTrigger value="diagnose">Diagnose</TabsTrigger>
          <TabsTrigger value="mapping">Sync-Mapping</TabsTrigger>
        </TabsList>

        <TabsContent value="diagnose" className="space-y-6 pt-4">
          {error && (
            <ErrorState
              title="Diagnose fehlgeschlagen"
              description={error}
              details={errorDetails ?? undefined}
              onRetry={() => void load(crossCheck)}
            />
          )}

          {loading && !data ? (
            <LoadingState />
          ) : data ? (
            <>
              <Card>
            <Card.Header>
              <div className="flex flex-col gap-1">
                <h2 className="text-h4 text-gray-900">Eigene Sync-Konfiguration</h2>
                <p className="text-caption text-gray-500">
                  Stand: {formatDateTime(data.generatedAt)}
                </p>
              </div>
              {hasOwnIssues ? (
                <Badge variant="danger" className="gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {data.currentUser.mismatches.length} Problem
                  {data.currentUser.mismatches.length === 1 ? '' : 'e'}
                </Badge>
              ) : (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  keine Auffaelligkeiten
                </Badge>
              )}
            </Card.Header>
            <Card.Body>
              <SimpleTable>
                <SimpleTable.Body>
                  <KeyValueRow
                    label="SysMan-User"
                    value={`${data.currentUser.name} (${data.currentUser.email})`}
                  />
                  <KeyValueRow label="SysMan-User-ID" value={<code>{data.currentUser.id}</code>} />
                  <KeyValueRow
                    label="users.tanss_id"
                    value={
                      data.currentUser.tanssId ?? (
                        <span className="text-red-700 font-medium">nicht gepflegt</span>
                      )
                    }
                  />
                  <KeyValueRow
                    label="users.m365_user_id"
                    value={
                      data.currentUser.m365UserId ?? (
                        <span className="text-gray-500">nicht gesetzt</span>
                      )
                    }
                  />
                  <KeyValueRow
                    label="M365-Token hinterlegt"
                    value={data.currentUser.hasM365Token ? 'ja' : 'nein'}
                  />
                  <KeyValueRow
                    label="Graph /me (Live)"
                    value={
                      data.currentUser.graphMe ? (
                        <div className="space-y-0.5">
                          <div>
                            <span className="text-caption text-gray-500">mail:</span>{' '}
                            <code>{data.currentUser.graphMe.mail ?? '–'}</code>
                          </div>
                          <div>
                            <span className="text-caption text-gray-500">userPrincipalName:</span>{' '}
                            <code>{data.currentUser.graphMe.userPrincipalName ?? '–'}</code>
                          </div>
                          <div>
                            <span className="text-caption text-gray-500">displayName:</span>{' '}
                            {data.currentUser.graphMe.displayName ?? '–'}
                          </div>
                          <div>
                            <span className="text-caption text-gray-500">id:</span>{' '}
                            <code>{data.currentUser.graphMe.id}</code>
                          </div>
                        </div>
                      ) : (
                        <span className="text-red-700">
                          {data.currentUser.graphMeError ?? '–'}
                        </span>
                      )
                    }
                  />
                  <KeyValueRow
                    label="TANSS-Employee-ID (aktiv)"
                    value={
                      data.currentUser.resolvedTanssEmployeeId != null ? (
                        <div className="space-y-0.5">
                          <div>{data.currentUser.resolvedTanssEmployeeId}</div>
                          <div className="text-caption text-gray-500">
                            Quelle: {tanssSourceLabel(data.currentUser.resolvedTanssSource)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-red-700">{data.currentUser.tanssError ?? '–'}</span>
                      )
                    }
                  />
                </SimpleTable.Body>
              </SimpleTable>

              {data.currentUser.mismatches.length > 0 && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-800">
                  <div className="flex items-center gap-2 font-semibold">
                    <ShieldAlert className="w-4 h-4" />
                    Auffaelligkeiten
                  </div>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {data.currentUser.mismatches.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card.Body>
          </Card>

          {data.isAdmin && data.conflicts && (
            <Card>
              <Card.Header>
                <div className="flex flex-col gap-1">
                  <h2 className="text-h4 text-gray-900">Globale Sync-Konflikte (Admin)</h2>
                  <p className="text-caption text-gray-500">
                    Pflichtdaten-Probleme, die fuer alle aktiven internen User gleichzeitig
                    gelten.
                  </p>
                </div>
                {hasGlobalIssues ? (
                  <Badge variant="danger" className="gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Probleme gefunden
                  </Badge>
                ) : (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    keine Konflikte
                  </Badge>
                )}
              </Card.Header>
              <Card.Body className="space-y-6">
                <section>
                  <h3 className="text-body font-semibold mb-2">
                    Doppelte <code>users.tanss_id</code>
                  </h3>
                  {data.conflicts.duplicateTanssIds.length === 0 ? (
                    <p className="text-small text-gray-500">Keine Duplikate.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.conflicts.duplicateTanssIds.map((dup) => (
                        <div
                          key={dup.tanssId}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-2"
                        >
                          <div className="text-small font-semibold">
                            tanss_id <code>{dup.tanssId}</code> — {dup.users.length} Treffer
                          </div>
                          <ul className="mt-1 list-disc pl-5 text-small text-gray-800">
                            {dup.users.map((u) => (
                              <li key={u.id}>
                                {u.name} &lt;{u.email}&gt;
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section>
                  <h3 className="text-body font-semibold mb-2">
                    User ohne <code>tanss_id</code>, aber mit M365-Token
                  </h3>
                  <p className="text-caption text-gray-500 mb-2">
                    Diese User wuerden im Sync auf den Session-Fallback fallen — dort kann
                    eine fremde TANSS-Employee-ID gezogen werden.
                  </p>
                  {data.conflicts.usersWithoutTanssIdButM365.length === 0 ? (
                    <p className="text-small text-gray-500">Keine.</p>
                  ) : (
                    <ul className="list-disc pl-5 text-small">
                      {data.conflicts.usersWithoutTanssIdButM365.map((u) => (
                        <li key={u.id}>
                          {u.name} &lt;{u.email}&gt;{' '}
                          <code className="text-caption text-gray-500">{u.id}</code>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <h3 className="text-body font-semibold mb-2">
                    Doppelte <code>users.m365_user_id</code>
                  </h3>
                  {data.conflicts.duplicateM365UserIds.length === 0 ? (
                    <p className="text-small text-gray-500">Keine Duplikate.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.conflicts.duplicateM365UserIds.map((dup) => (
                        <div
                          key={dup.m365UserId}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-2"
                        >
                          <div className="text-small font-semibold">
                            m365_user_id <code>{dup.m365UserId}</code> — {dup.users.length} Treffer
                          </div>
                          <ul className="mt-1 list-disc pl-5 text-small text-gray-800">
                            {dup.users.map((u) => (
                              <li key={u.id}>
                                {u.name} &lt;{u.email}&gt;
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section>
                  <h3 className="text-body font-semibold mb-2">Token-Mailbox-Mismatches</h3>
                  <p className="text-caption text-gray-500 mb-2">
                    Graph <code>/me</code> mit dem gespeicherten Token liefert eine Mailbox,
                    die nicht zur SysMan-Email des Users passt. Genau diese Faelle erklaeren
                    „User A sieht Termine von User B in Outlook".
                  </p>
                  {data.conflicts.tokenMailboxMismatches.length === 0 ? (
                    <p className="text-small text-gray-500">Keine Mismatches.</p>
                  ) : (
                    <div className="space-y-2">
                      {data.conflicts.tokenMailboxMismatches.map((m) => (
                        <div
                          key={m.user.id}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small"
                        >
                          <div className="font-semibold">
                            {m.user.name} &lt;{m.user.email}&gt;
                          </div>
                          <div className="mt-1">{m.note}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>
              <div className="flex flex-col gap-1">
                <h2 className="text-h4 text-gray-900">
                  Deine gespiegelten Termine ({data.links.length})
                </h2>
                <p className="text-caption text-gray-500">
                  Aus <code>calendar_links</code> — Schluessel zwischen TANSS-Supports und
                  Outlook-Events deines Postfachs.
                </p>
              </div>
              <Switch
                checked={crossCheck}
                onChange={(e) => {
                  const v = e.target.checked
                  setCrossCheck(v)
                  void load(v)
                }}
                label="TANSS-Cross-Check (langsam)"
                hint="Holt fuer jeden Termin den TANSS-Support und vergleicht den Mitarbeiter."
              />
            </Card.Header>
            <Card.Body>
              {data.links.length === 0 ? (
                <EmptyState title="Keine gespiegelten Termine im Fenster." />
              ) : (
                <SimpleTable>
                  <SimpleTable.Head>
                    <SimpleTable.Tr>
                      <SimpleTable.Th>Origin</SimpleTable.Th>
                      <SimpleTable.Th>TANSS-Support</SimpleTable.Th>
                      <SimpleTable.Th>Titel</SimpleTable.Th>
                      <SimpleTable.Th>Letzter Sync</SimpleTable.Th>
                      <SimpleTable.Th>TANSS-Mitarbeiter</SimpleTable.Th>
                    </SimpleTable.Tr>
                  </SimpleTable.Head>
                  <SimpleTable.Body>
                    {data.links.map((l) => (
                      <SimpleTable.Tr key={l.id}>
                        <SimpleTable.Td>
                          <Badge variant={l.origin === 'tanss' ? 'info' : 'accent'}>
                            {l.origin}
                          </Badge>
                        </SimpleTable.Td>
                        <SimpleTable.Td>
                          <code>#{l.entityId}</code>
                        </SimpleTable.Td>
                        <SimpleTable.Td>
                          {l.originalTitle ?? <span className="text-gray-400">–</span>}
                        </SimpleTable.Td>
                        <SimpleTable.Td>{formatDateTime(l.lastSyncedAt)}</SimpleTable.Td>
                        <SimpleTable.Td>
                          {data.tanssCrossCheckPerformed ? (
                            l.tanssLookupError ? (
                              <span className="text-amber-700">{l.tanssLookupError}</span>
                            ) : l.tanssEmployeeId != null ? (
                              <span
                                className={
                                  l.tanssEmployeeIdMatchesUser === false
                                    ? 'text-red-700 font-semibold'
                                    : 'text-gray-800'
                                }
                              >
                                {l.tanssEmployeeId}
                                {l.tanssEmployeeIdMatchesUser === false && ' ≠ Du'}
                                {l.tanssEmployeeIdMatchesUser === true && ' (Du)'}
                              </span>
                            ) : (
                              <span className="text-gray-400">–</span>
                            )
                          ) : (
                            <span className="text-gray-400">Cross-Check aktivieren</span>
                          )}
                        </SimpleTable.Td>
                      </SimpleTable.Tr>
                    ))}
                  </SimpleTable.Body>
                </SimpleTable>
              )}
            </Card.Body>
          </Card>

          {data.isAdmin && <MirrorInspectorCard />}
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="mapping" className="pt-4">
          <SyncRegistryCard />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─── Sync-Registrierung (eigene Eintraege) ──────────────────────────────────

interface SyncRegistryEntry {
  id: string
  outlookId: string | null
  tanssId: string | null
  title: string | null
  origin: 'outlook' | 'tanss'
  status: 'active' | 'decoupled' | 'deleted'
  planningType: string | null
  start: string | null
  end: string | null
  lastSyncedAt: string | null
  createdAt: string | null
  deletedAt: string | null
}

function statusBadge(status: SyncRegistryEntry['status']) {
  if (status === 'active') return <Badge variant="success">Aktiv</Badge>
  if (status === 'decoupled') return <Badge variant="warning">Entkoppelt</Badge>
  return <Badge variant="neutral" className="text-gray-500">Geloescht</Badge>
}

function buildRegistryColumns(
  onDecouple: (id: string) => void,
  decoupling: Set<string>,
): ColumnDef<SyncRegistryEntry>[] {
  return [
    {
      id: 'title',
      header: 'Titel',
      accessorKey: 'title',
      cell: ({ row }) => (
        <div className={`min-w-0 ${row.original.status === 'deleted' ? 'opacity-50' : ''}`}>
          <div className="text-small font-medium truncate max-w-xs">
            {row.original.title ?? <span className="text-gray-400 italic">kein Titel</span>}
          </div>
          <div className="text-caption text-gray-500 mt-0.5">
            {row.original.planningType ?? '–'}
          </div>
        </div>
      ),
    },
    {
      id: 'start',
      header: 'Datum',
      accessorKey: 'start',
      cell: ({ row }) => {
        const d = row.original.start ? new Date(row.original.start) : null
        if (!d) return <span className="text-gray-400 text-caption">–</span>
        return (
          <div className={`text-small whitespace-nowrap ${row.original.status === 'deleted' ? 'opacity-50' : ''}`}>
            {d.toLocaleDateString('de-DE')}{' '}
            <span className="text-gray-500">{d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )
      },
    },
    {
      id: 'origin',
      header: 'Herkunft',
      accessorKey: 'origin',
      cell: ({ row }) => (
        <Badge variant={row.original.origin === 'outlook' ? 'info' : 'neutral'}>
          {row.original.origin === 'outlook' ? 'Outlook' : 'TANSS'}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => statusBadge(row.original.status),
    },
    {
      id: 'outlookId',
      header: 'Outlook-ID',
      accessorKey: 'outlookId',
      cell: ({ row }) =>
        row.original.outlookId ? (
          <code className="text-caption" title={row.original.outlookId}>
            {row.original.outlookId.slice(0, 8)}&hellip;
          </code>
        ) : (
          <span className="text-gray-400 text-caption">–</span>
        ),
    },
    {
      id: 'tanssId',
      header: 'TANSS-ID',
      accessorKey: 'tanssId',
      cell: ({ row }) =>
        row.original.tanssId ? (
          <code className="text-caption">{row.original.tanssId}</code>
        ) : (
          <span className="text-gray-400 text-caption">–</span>
        ),
    },
    {
      id: 'sysmanId',
      header: 'SysMan-ID',
      accessorKey: 'id',
      cell: ({ row }) => (
        <code className="text-caption" title={row.original.id}>
          {row.original.id.slice(0, 8)}&hellip;
        </code>
      ),
    },
    {
      id: 'lastSyncedAt',
      header: 'Letzter Sync',
      accessorKey: 'lastSyncedAt',
      cell: ({ row }) => {
        const d = row.original.lastSyncedAt ? new Date(row.original.lastSyncedAt) : null
        if (!d) return <span className="text-gray-400 text-caption">–</span>
        return <span className="text-caption whitespace-nowrap">{d.toLocaleString('de-DE')}</span>
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        if (row.original.status !== 'active') return null
        const busy = decoupling.has(row.original.id)
        return (
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<Unlink className="w-3 h-3" />}
            loading={busy}
            onClick={() => onDecouple(row.original.id)}
            aria-label="Manuell entkoppeln"
          >
            Entkoppeln
          </Button>
        )
      },
    },
  ]
}

function SyncRegistryCard() {
  const [entries, setEntries] = useState<SyncRegistryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [decoupling, setDecoupling] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<SyncRegistryEntry[]>('/calendar-sync/registry')
      setEntries(data)
    } catch (err) {
      setError(extractErrorMessage(err) || 'Sync-Registrierung konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleDecouple = useCallback(async (id: string) => {
    setDecoupling((prev) => new Set(prev).add(id))
    try {
      await apiClient.post(`/calendar-sync/registry/${id}/decouple`, {})
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: 'decoupled' as const, tanssId: null } : e)),
      )
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(msg || 'Entkoppeln fehlgeschlagen.')
    } finally {
      setDecoupling((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }, [])

  const columns = useMemo(
    () => buildRegistryColumns(handleDecouple, decoupling),
    [handleDecouple, decoupling],
  )

  const activeCount = entries.filter((e) => e.status === 'active').length
  const decoupledCount = entries.filter((e) => e.status === 'decoupled').length
  const deletedCount = entries.filter((e) => e.status === 'deleted').length

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-col gap-1">
          <h2 className="text-h4 text-gray-900">Sync-Mapping</h2>
          <p className="text-caption text-gray-500">
            Alle Outlook&harr;TANSS-Verknuepfungen Deines Accounts: aktive, entkoppelte und geloeschte Eintraege.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeCount > 0 && <Badge variant="success">{activeCount} aktiv</Badge>}
          {decoupledCount > 0 && <Badge variant="warning">{decoupledCount} entkoppelt</Badge>}
          {deletedCount > 0 && <Badge variant="neutral">{deletedCount} geloescht</Badge>}
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<RefreshCw className="w-4 h-4" />}
            loading={loading}
            onClick={() => void load()}
          >
            Neu laden
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {error && (
          <ErrorState title="Fehler" description={error} onRetry={() => void load()} />
        )}
        {loading && entries.length === 0 ? (
          <LoadingState />
        ) : entries.length === 0 ? (
          <EmptyState
            title="Keine Eintraege"
            description="Es wurden noch keine Outlook-TANSS-Verknuepfungen fuer Deinen Account angelegt."
          />
        ) : (
          <DataTable
            columns={columns}
            data={entries}
            pagination={{ pageSize: 20 }}
          />
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Admin-Sektion: Outlook→TANSS-Spiegelungen pro User ────────────────────

function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '–'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

function MirrorInspectorCard() {
  const [usersList, setUsersList] = useState<InternalUserRow[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [usersError, setUsersError] = useState<string | null>(null)

  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [days, setDays] = useState<string>('14')
  // Default: an. Der Live-Lauf persistiert die Outlook-Originalfelder in
  // calendar_links zurueck — danach klappt auch der schnelle Modus (Live aus)
  // mit Datum + Subject. Wer Performance braucht, kann ihn manuell ausschalten.
  const [live, setLive] = useState(true)

  const [result, setResult] = useState<UserMirrorsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setUsersLoading(true)
    setUsersError(null)
    apiClient
      .get<InternalUserRow[]>('/users', { params: { portalKind: 'internal' } })
      .then(({ data }) => {
        if (cancelled) return
        const active = data.filter((u) => u.isActive)
        active.sort((a, b) => a.name.localeCompare(b.name, 'de'))
        setUsersList(active)
      })
      .catch((err) => {
        if (cancelled) return
        const msg = extractErrorMessage(err)
        setUsersError(`Benutzerliste konnte nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const runLookup = useCallback(async () => {
    if (!selectedUserId) return
    setLoading(true)
    setError(null)
    setErrorDetails(null)
    setResult(null)
    try {
      const { data } = await apiClient.get<UserMirrorsResponse>(
        '/calendar-sync/diagnostics/mirrors',
        {
          params: {
            userId: selectedUserId,
            days: days,
            ...(live ? { live: '1' } : {}),
          },
        },
      )
      setResult(data)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Spiegelungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      setErrorDetails(msg || null)
    } finally {
      setLoading(false)
    }
  }, [selectedUserId, days, live])

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-col gap-1">
          <h2 className="text-h4 text-gray-900">Outlook→TANSS-Spiegelungen pro User (Admin)</h2>
          <p className="text-caption text-gray-500">
            Welche Eintraege wurden aus dem Outlook-Kalender eines Users in den letzten Tagen
            nach TANSS gespiegelt? Zeigt die <code>calendar_links</code> mit <code>origin=outlook</code>;
            der Live-Modus laedt zusaetzlich den aktuellen TANSS-Support pro Eintrag.
          </p>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {usersError && <ErrorState title="Fehler" description={usersError} />}

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end">
          <Select
            label="Benutzer"
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            placeholder={usersLoading ? 'Lade …' : 'Benutzer waehlen'}
            disabled={usersLoading || usersList.length === 0}
            options={usersList.map((u) => ({
              value: u.id,
              label: `${u.name} <${u.email}>`,
            }))}
          />
          <Input
            label="Tage zurueck"
            type="number"
            min={1}
            max={180}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            hint="1–180"
          />
          <Button
            variant="primary"
            size="md"
            leadingIcon={<Search className="w-4 h-4" />}
            loading={loading}
            disabled={!selectedUserId || loading}
            onClick={() => void runLookup()}
          >
            Abrufen
          </Button>
        </div>

        <Switch
          checked={live}
          onChange={(e) => setLive(e.target.checked)}
          label="Live-TANSS-Details laden (langsam, max. 100 Supports)"
          hint="Liest pro Spiegelung den aktuellen TANSS-Support (text, planningType, employeeId)."
        />

        {error && (
          <ErrorState
            title="Abruf fehlgeschlagen"
            description={error}
            details={errorDetails ?? undefined}
            onRetry={() => void runLookup()}
          />
        )}

        {result && (
          <div className="space-y-3">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-small">
              <div className="font-semibold">
                {result.user.name} &lt;{result.user.email}&gt;
              </div>
              <div className="text-caption text-gray-600">
                {result.totalCount} Spiegelung{result.totalCount === 1 ? '' : 'en'} seit{' '}
                {formatDateTime(result.since)} ·{' '}
                <code>users.tanss_id = {result.user.tanssId ?? '∅'}</code>
                {result.liveDetailsLoaded ? ' · mit Live-Details' : ''}
              </div>
            </div>

            {result.entries.length === 0 ? (
              <EmptyState title="Keine Outlook→TANSS-Spiegelungen im Zeitraum." />
            ) : (
              <div className="space-y-3">
                {result.entries.map((entry) => (
                  <MirrorEntryRow key={entry.linkId} entry={entry} />
                ))}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Einzelne Spiegelungs-Karte ───────────────────────────────────────────

function MirrorEntryRow({ entry }: { entry: MirrorEntry }) {
  const tanssStart = entry.tanssSupport?.date != null
    ? new Date(entry.tanssSupport.date * 1000)
    : null
  const outlookStart = entry.outlookEvent?.start ? new Date(entry.outlookEvent.start) : null
  const outlookEnd = entry.outlookEvent?.end ? new Date(entry.outlookEvent.end) : null
  // Persistent gespeicherte Originalzeit aus `calendar_links` — wird beim
  // Outlook→TANSS-Sync mitgeschrieben und erlaubt eine Anzeige von Datum/Dauer
  // ohne Live-Lookup.
  const persistedStart = entry.originalStart ? new Date(entry.originalStart) : null
  const persistedEnd = entry.originalEnd ? new Date(entry.originalEnd) : null
  const termStart = outlookStart ?? persistedStart ?? tanssStart
  const termDurationMin = entry.tanssSupport?.duration
    ?? (outlookStart && outlookEnd
      ? Math.max(1, Math.round((outlookEnd.getTime() - outlookStart.getTime()) / 60_000))
      : null)
    ?? (persistedStart && persistedEnd
      ? Math.max(1, Math.round((persistedEnd.getTime() - persistedStart.getTime()) / 60_000))
      : null)
  const originLabel = entry.origin === 'outlook' ? 'Outlook → TANSS' : 'TANSS → Outlook'
  const originVariant: 'accent' | 'info' = entry.origin === 'outlook' ? 'accent' : 'info'

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-3 py-2 text-small">
        <Badge variant={originVariant}>{originLabel}</Badge>
        {entry.entityType === 'tanss_support_decoupled' && (
          <Badge variant="warning">decoupled</Badge>
        )}
        <span className="text-gray-900 font-medium">
          {termStart ? termStart.toLocaleString('de-DE') : '– keine Zeit –'}
        </span>
        <span className="text-caption text-gray-500">
          Dauer: {termDurationMin != null ? formatDuration(termDurationMin) : '–'}
        </span>
        <span className="ml-auto flex items-center gap-3 text-caption">
          <span className="text-gray-500">
            angelegt: {formatDateTime(entry.createdAt)}
          </span>
          {entry.tanssWebUrl ? (
            <a
              href={entry.tanssWebUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 hover:underline"
            >
              TANSS-Support #{entry.tanssSupportId} oeffnen ↗
            </a>
          ) : (
            <code className="text-gray-500">#{entry.tanssSupportId}</code>
          )}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div className="px-3 py-2 border-b md:border-b-0 md:border-r border-gray-100">
          <div className="text-caption text-gray-500 uppercase tracking-wide mb-1">Outlook</div>
          {entry.outlookLookupError ? (
            <div className="text-small text-amber-700">{entry.outlookLookupError}</div>
          ) : entry.outlookEvent ? (
            <div className="space-y-1 text-small">
              <div className="font-medium text-gray-900">
                {entry.outlookEvent.subject ?? <span className="text-gray-400">(ohne Subject)</span>}
              </div>
              <div className="text-caption text-gray-500">
                {outlookStart ? outlookStart.toLocaleString('de-DE') : '–'}
                {outlookEnd ? ` – ${outlookEnd.toLocaleString('de-DE')}` : ''}
                {entry.outlookEvent.isAllDay ? ' · ganztaegig' : ''}
              </div>
              {entry.outlookEvent.bodyPreview && (
                <div className="text-caption text-gray-700 whitespace-pre-wrap">
                  {entry.outlookEvent.bodyPreview}
                </div>
              )}
              {entry.outlookEvent.webLink && (
                <div>
                  <a
                    href={entry.outlookEvent.webLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-caption text-brand-700 hover:underline"
                  >
                    In Outlook oeffnen ↗
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="text-small text-gray-700">
              <div className="font-medium">
                {entry.originalTitle ?? <span className="text-gray-400">(Subject wurde nicht gespeichert)</span>}
              </div>
              {(persistedStart || persistedEnd) && (
                <div className="text-caption text-gray-500 mt-0.5">
                  {persistedStart ? persistedStart.toLocaleString('de-DE') : '–'}
                  {persistedEnd ? ` – ${persistedEnd.toLocaleString('de-DE')}` : ''}
                </div>
              )}
              <div className="text-caption text-gray-500 mt-1">
                Live-Details nicht angefordert. m365-EventId:{' '}
                <code>{entry.m365EventId.slice(0, 24)}…</code>
              </div>
            </div>
          )}
        </div>
        <div className="px-3 py-2">
          <div className="text-caption text-gray-500 uppercase tracking-wide mb-1">TANSS</div>
          {entry.tanssLookupError ? (
            <div className="text-small text-amber-700">{entry.tanssLookupError}</div>
          ) : entry.tanssSupport ? (
            <div className="space-y-1 text-small">
              <div className="text-caption text-gray-500">
                <code>{entry.tanssSupport.planningType}</code>
                {' · empId: '}{entry.tanssSupport.employeeId}
                {entry.tanssSupport.ticketId
                  ? <> · ticketId: <code>{entry.tanssSupport.ticketId}</code></>
                  : null}
              </div>
              {tanssStart && (
                <div className="text-caption text-gray-500">
                  Start: {tanssStart.toLocaleString('de-DE')}
                </div>
              )}
              {entry.tanssSupport.text && (
                <div className="text-caption text-gray-700 whitespace-pre-wrap">
                  {entry.tanssSupport.text}
                </div>
              )}
            </div>
          ) : (
            <div className="text-small text-gray-400">
              Live-Details nicht angefordert.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CalendarDiagnosticsPage
